import React, { ReactElement, Ref, RefAttributes, WeakValidationMap } from 'react';
import PropTypes, { Validator } from 'prop-types';
import LabelButton from '../LabelButton';
import { forgeClassHelper } from '../utils/classes';
import forwardRefToProps from '../utils/forwardRefToProps';
import ExpandSmall from '@athena/forge-icons/dist/ExpandSmall';

const classes = forgeClassHelper('menu-trigger');

export type MenuTriggerProps = {
  /** Set to true to style button as active */
  active?: boolean;

  /** Specify which side(s) of the trigger's button should have a border radius */
  borderRadius?: 'all' | 'left' | 'right' | 'top' | 'bottom' | 'none';

  /** Function called when the trigger button is clicked */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;

  /** Trigger label text */
  label?: string;

  /** Trigger size */
  size?: 'small' | 'medium' | 'large';
};

export type MenuTriggerComponentProps = MenuTriggerProps & {
  /** Ref forwarded to the Menu */
  forwardedRef?: Ref<HTMLButtonElement>;
};

/**
 * MenuTrigger
 *
 * @documentedBy Menu
 */
export const MenuTriggerComponent = function ({
  borderRadius = 'all',
  active = false,
  onClick,
  size = 'medium',
  label,
  forwardedRef,
}: MenuTriggerComponentProps): ReactElement {
  return (
    <LabelButton
      borderRadius={borderRadius}
      active={active}
      size={size}
      {...classes({ modifiers: { [size]: true, active: active } })}
      onClick={onClick}
      ref={forwardedRef}
    >
      {label}
      <ExpandSmall {...classes({ element: 'icon' })} />
    </LabelButton>
  );
};

export const menuTriggerPropTypes: WeakValidationMap<MenuTriggerComponentProps> = {
  /** Set to true to style button as active */
  active: PropTypes.bool,

  /** Specify which side(s) of the trigger's button should have a border radius */
  borderRadius: PropTypes.oneOf(['all', 'left', 'right', 'top', 'bottom', 'none']),

  /** Function called when the trigger button is clicked */
  onClick: PropTypes.func,

  /** Trigger label text */
  label: PropTypes.string,

  /** Trigger size */
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /** Ref forwarded to the Menu */
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    /** Note: { current: PropTypes.instanceOf(HTMLButtonElement) } would be more
     *  correct, but causes a StackOverflow when building gatsby.
     */
    PropTypes.shape({ current: PropTypes.any }),
  ]) as Validator<Ref<HTMLButtonElement>>,
};

MenuTriggerComponent.propTypes = menuTriggerPropTypes;

MenuTriggerComponent.displayName = 'MenuTrigger';

export interface MenuTriggerDefaultExport {
  (props: MenuTriggerProps & RefAttributes<HTMLButtonElement>): ReactElement;
  supportedProps?: Array<keyof MenuTriggerProps>;
}

const MenuTrigger = forwardRefToProps(MenuTriggerComponent) as MenuTriggerDefaultExport;
MenuTrigger.supportedProps = ['borderRadius', 'active', 'size'];
export default MenuTrigger;
